import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiAward,FiUsers,FiTrendingUp,FiArrowRight,FiShield,FiHeart,FiTarget}=FiIcons;

const AboutPage=()=> {
  const values=[
    {
      icon: FiShield,
      title: 'Qualität',
      description: 'Höchste Standards in Material und Ausführung sind unser Anspruch bei jedem Projekt.'
    },
    {
      icon: FiHeart,
      title: 'Leidenschaft',
      description: 'Wir lieben unser Handwerk und setzen uns mit vollem Engagement für Ihre Projekte ein.'
    },
    {
      icon: FiTarget,
      title: 'Präzision',
      description: 'Exakte Planung und sorgfältige Umsetzung garantieren optimale Ergebnisse.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section with Background Video */}
      <section className="relative py-32 bg-gradient-to-br from-[#3466ad] to-[#2a5291] text-white overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <motion.video
            initial={{opacity: 0}}
            animate={{opacity: 0.6}}
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="https://canv.wasacon.com/rudt-bau/_assets/video/792b9cb17c1c9ab499955beb399154cf.mp4" type="video/mp4" />
          </motion.video>
        </div>

        {/* Enhanced Dark Overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Über RUDT Bau
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto">
              Qualität, Präzision und Handwerk seit 2022 - Ihr zuverlässiger Partner für hochwertige Bauarbeiten.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{opacity: 0,x: -30}}
              whileInView={{opacity: 1,x: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Unsere Geschichte</h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Waldemar Rudt absolvierte im Jahr 2000 seine Ausbildung zum Maurer und sammelte über zwei Jahrzehnte umfassende praktische Erfahrung im Baugewerbe. Bis 2022 war er als Maurer in zwei renommierten Unternehmen tätig: zunächst bei Caspar Köchling in Dortmund, später bei Bernd Winkens. In dieser Zeit arbeitete er an zahlreichen Bauprojekten und entwickelte ein ausgeprägtes Verständnis für präzises Handwerk, zuverlässige Ausführung und hohe Qualitätsstandards.
                </p>
                <p>
                  Seit dem 1. April 2022 ist Waldemar Rudt selbstständig tätig und führt das Unternehmen RUDT Bau.
                </p>
                <p>
                  Der Erfolg des Unternehmens basiert auf drei Säulen: erstklassige Handwerkskunst, zuverlässige Projektabwicklung und absolute Kundenzufriedenheit. Diese Werte prägen die tägliche Arbeit und jedes einzelne Projekt.
                </p>
                <p>
                  Heute beschäftigt RUDT Bau 4 qualifizierte Fachkräfte und hat bereits über 40 Projekte erfolgreich realisiert. Dabei bleibt das Unternehmen seinem hohen Anspruch an Qualität, Präzision und fachgerechte Ausführung stets treu.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{opacity: 0,x: 30}}
              whileInView={{opacity: 1,x: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8,delay: 0.2}}
              className="relative"
            >
              <img
                src="https://canv.wasacon.com/rudt-bau/_assets/media/45e5af10c8d00190ecd5c35d2c5c7c03.jpg"
                alt="RUDT Bau Firmengebäude"
                className="w-full h-96 object-cover rounded-xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Unsere Werte</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Diese Grundsätze prägen unser tägliches Handeln und machen uns zu einem vertrauenswürdigen Partner für Ihr Bauvorhaben.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value,index)=> (
              <motion.div
                key={value.title}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8,delay: index * 0.2}}
                className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#3466ad] to-[#2a5291] rounded-xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">RUDT Bau in Zahlen</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{opacity: 0,y: 30}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8,delay: 0.1}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">3+</div>
              <div className="text-gray-600 font-medium">Jahre Selbstständigkeit</div>
            </motion.div>

            <motion.div
              initial={{opacity: 0,y: 30}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8,delay: 0.2}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">4</div>
              <div className="text-gray-600 font-medium">Fachkräfte im Team</div>
            </motion.div>

            <motion.div
              initial={{opacity: 0,y: 30}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8,delay: 0.3}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAward} className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">40+</div>
              <div className="text-gray-600 font-medium">Projekte realisiert</div>
            </motion.div>

            <motion.div
              initial={{opacity: 0,y: 30}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8,delay: 0.4}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiHeart} className="w-8 h-8 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">20+</div>
              <div className="text-gray-600 font-medium">Jahre Berufserfahrung</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#3466ad] to-[#2a5291] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Werden Sie Teil unserer Erfolgsgeschichte</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
              Lassen Sie uns gemeinsam Ihr nächstes Bauvorhaben realisieren. Kontaktieren Sie uns für eine unverbindliche Beratung.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-2 bg-white text-[#3466ad] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors duration-200"
            >
              <span>Jetzt Kontakt aufnehmen</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;